import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { waxPrintTexture } from '../world/textures.js';

// Skinned, animated people from the Quaternius Universal Base Characters (CC0): two bodies and one
// library of clips on the same 65-joint skeleton. A fixed pool of bodies is handed to whoever is
// nearest the player (the near ring); everyone further away keeps the instanced box figure, which
// at that distance is a few pixels tall. Loads in the background; until then the boxes stay.
// The pack's body is bare skin, so clothes are cut from it by bone: the vertices the spine and
// upper arms move wear the shirt (or kaba), those of the hips and legs the trousers (or wrapper),
// the feet the shoes; head, hands and forearms stay skin, tinted to the person's tone.
// Hair comes from the pack's hairstyles baked into head-bone space (scripts/build-hair.mjs): every
// body carries all six styles under its head bone and shows the one its person was dealt.

const CLIPS = { idle: 'Idle_Loop', walk: 'Walk_Loop', jog: 'Jog_Fwd_Loop', run: 'Sprint_Loop', talk: 'Idle_Talking_Loop', sit: 'Sitting_Idle_Loop', drive: 'Driving_Loop' };
// skin tones of the North West, as tints over the pack's base texture
const SKINS = ['#9a6a4a', '#7f5236', '#6b4530', '#5a3a28', '#4a2f22', '#8a5e42'].map((h) => new THREE.Color(h));
const SHOES = ['#1a1a1a', '#3a2a1e', '#2a2a30', '#5a4a3a'].map((h) => new THREE.Color(h));
const BODY_HEIGHT = 1.72; // metres the pack's body stands
const GROUPS = ['top', 'bottom', 'shoes', 'skin'];
// hair as tints over the pack's strand texture: black to dark brown, and grey for the old
const HAIRS = ['#120d0a', '#1a110c', '#231610', '#0e0a08', '#2a1a12'].map((h) => new THREE.Color(h));
const GREY = new THREE.Color('#8a8580');
const HAIR_STYLES = ['buns', 'long', 'buzzedFemale', 'buzzed', 'parted', 'beard'];

// The style a person wears, from two seeds in 0..1: women in buns, long braids or cropped hair,
// one in five bare-headed; men mostly buzzed, some parted, one in four shaved; beards on a third
// of the men.
export function hairFor(sex, seed, seed2) {
  const style = sex === 0 ? (seed < 0.35 ? 'buns' : seed < 0.6 ? 'long' : seed < 0.8 ? 'buzzedFemale' : null) : seed < 0.55 ? 'buzzed' : seed < 0.75 ? 'parted' : null;
  return { style, beard: sex !== 0 && seed2 < 0.33 };
}

// which clothing group a bone's vertices belong to
function groupOfBone(name) {
  if (/^(spine_0[123]|clavicle_|upperarm_)/.test(name)) return 0;
  if (/^(pelvis|thigh_|calf_)/.test(name)) return 1;
  if (/^(foot_|ball_)/.test(name)) return 2;
  return 3;
}

let loader = null;
function getLoader() {
  if (!loader) {
    loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
  }
  return loader;
}

export class Characters {
  // size: bodies in the pool (the near ring). Nothing is drawn until load() has finished.
  constructor(scene, size = 24) {
    this.scene = scene;
    this.size = size;
    this.ready = false;
    this.bodies = { male: null, female: null }; // the loaded template scenes
    this.clips = new Map();
    this.hair = {}; // style key -> template mesh in head-bone space
    this.pool = []; // { root, mixer, actions, busy, sex, current, skin, cloth, hair, hairMaterial }
    this.failed = false;
    this.load();
  }

  async load() {
    try {
      const gl = getLoader();
      const [male, female, anim, hair] = await Promise.all([
        gl.loadAsync('assets/characters/male.glb'),
        gl.loadAsync('assets/characters/female.glb'),
        gl.loadAsync('assets/characters/animations.glb'),
        gl.loadAsync('assets/characters/hair.glb').catch((e) => {
          console.warn('hair did not load; heads stay bare', e);
          return null;
        }),
      ]);
      for (const clip of anim.animations) this.clips.set(clip.name, clip);
      if (hair) {
        for (const key of HAIR_STYLES) {
          const mesh = hair.scene.getObjectByName(key);
          if (mesh && mesh.isMesh) this.hair[key] = mesh;
        }
      }
      this.bodies.male = this.prepare(male.scene);
      this.bodies.female = this.prepare(female.scene);
      for (let i = 0; i < this.size; i++) this.pool.push(this.make(i % 2 === 0 ? 'male' : 'female'));
      this.ready = true;
    } catch (e) {
      console.warn('characters did not load; the box figures stay', e);
      this.failed = true;
    }
  }

  // Scales a template to its real height, cuts the body into clothing groups and sets its
  // materials up for shadows.
  prepare(root) {
    const box = new THREE.Box3().setFromObject(root);
    const scale = BODY_HEIGHT / Math.max(0.01, box.max.y - box.min.y);
    root.scale.setScalar(scale);
    root.updateMatrixWorld(true);
    root.traverse((o) => {
      if (!o.isMesh) return;
      o.castShadow = true;
      o.receiveShadow = false;
      o.frustumCulled = true;
      if (o.material) o.material.side = THREE.FrontSide;
      if (o.isSkinnedMesh && o.material && /superhero/i.test(o.material.name)) {
        o.userData.body = true;
        groupByBone(o);
      }
    });
    return root;
  }

  make(sex) {
    const root = cloneSkeleton(this.bodies[sex]);
    const mixer = new THREE.AnimationMixer(root);
    const actions = {};
    for (const [key, name] of Object.entries(CLIPS)) {
      const clip = this.clips.get(name);
      if (clip) actions[key] = mixer.clipAction(clip);
    }
    // each body gets its own clothes and skin materials, so it can be dressed and tinted alone
    let skin = null;
    const cloth = [];
    root.traverse((o) => {
      if (!o.userData.body) return;
      const base = Array.isArray(o.material) ? o.material[0] : o.material;
      const top = new THREE.MeshLambertMaterial({ color: 0xffffff });
      const bottom = new THREE.MeshLambertMaterial({ color: 0xffffff });
      const shoes = new THREE.MeshLambertMaterial({ color: 0x1a1a1a });
      skin = base.clone();
      o.material = [top, bottom, shoes, skin];
      cloth.push(top, bottom, shoes);
    });
    // every style hangs under the head bone, hidden until the body is dealt one; one material per
    // body so its hair and beard take the person's colour together
    const hair = {};
    const base = Object.values(this.hair)[0];
    const hairMaterial = new THREE.MeshLambertMaterial({ color: HAIRS[0], map: base && base.material && base.material.map ? base.material.map : null, side: THREE.DoubleSide });
    const head = root.getObjectByName('Head');
    if (head) {
      for (const [key, template] of Object.entries(this.hair)) {
        const mesh = new THREE.Mesh(template.geometry, hairMaterial);
        mesh.position.copy(template.position);
        mesh.quaternion.copy(template.quaternion);
        mesh.scale.copy(template.scale);
        mesh.castShadow = true;
        mesh.visible = false;
        head.add(mesh);
        hair[key] = mesh;
      }
    }
    root.visible = false;
    this.scene.add(root);
    return { root, mixer, actions, busy: false, sex, current: null, skin, cloth, hair, hairMaterial };
  }

  // Shows the hair a person was dealt (see hairFor) on a borrowed body, and nothing else.
  dress(body, sex, tone) {
    const look = hairFor(sex, (tone * 53) % 1, (tone * 17) % 1);
    const show = new Set([look.style, look.beard ? 'beard' : null].filter(Boolean));
    for (const [key, mesh] of Object.entries(body.hair)) mesh.visible = show.has(key);
    const old = (tone * 7) % 1 < 0.08;
    body.hairMaterial.color.copy(old ? GREY : HAIRS[Math.floor(((tone * 41) % 1) * HAIRS.length)]);
  }

  // Lends a body: sex 0 female, 1 male; tone 0..1; top and bottom the colours of the clothes
  // (THREE.Color or hex), or none for a seeded choice. Null when no body is free.
  borrow(sex, tone, top = null, bottom = null) {
    if (!this.ready) return null;
    const want = sex === 0 ? 'female' : 'male';
    const body = this.pool.find((b) => !b.busy && b.sex === want) || this.pool.find((b) => !b.busy);
    if (!body) return null;
    body.busy = true;
    body.root.visible = true;
    const tint = SKINS[Math.floor(tone * SKINS.length) % SKINS.length];
    if (body.skin) body.skin.color.copy(tint);
    this.dress(body, sex, tone);
    if (body.cloth.length) {
      body.cloth[0].color.set(top || (sex === 0 ? '#c8402f' : '#f4f1e8'));
      body.cloth[1].color.set(bottom || '#3a3a4a');
      body.cloth[2].color.copy(SHOES[Math.floor(tone * 7) % SHOES.length]);
      // women in wax print (kaba and wrapper alike), one man in five in a printed shirt
      const roll = (tone * 97) % 1;
      const print = sex === 0 ? waxPrintTexture(Math.floor(roll * 4)) : roll < 0.2 ? waxPrintTexture(Math.floor(roll * 20)) : null;
      const bottomPrint = sex === 0 ? waxPrintTexture(Math.floor(((tone * 31) % 1) * 4)) : null;
      for (const [i, map] of [[0, print], [1, bottomPrint]]) {
        if (body.cloth[i].map !== map) {
          body.cloth[i].map = map;
          body.cloth[i].needsUpdate = true;
        }
      }
    }
    this.play(body, 'idle', 0);
    return body;
  }

  giveBack(body) {
    if (!body) return;
    body.busy = false;
    body.root.visible = false;
    body.mixer.stopAllAction();
    body.current = null;
  }

  // Places a borrowed body and picks its clip from the speed it moves at.
  pose(body, x, y, z, yaw, speed, dt, talking = false) {
    body.root.position.set(x, y, z);
    body.root.rotation.set(0, yaw, 0);
    const key = speed > 4.5 ? 'run' : speed > 2.4 ? 'jog' : speed > 0.15 ? 'walk' : talking ? 'talk' : 'idle';
    this.play(body, key, 0.25);
    body.mixer.update(dt);
  }

  play(body, key, fade) {
    const next = body.actions[key] || body.actions.idle;
    if (!next || body.current === next) return;
    next.reset().setEffectiveWeight(1).fadeIn(fade).play();
    if (body.current) body.current.fadeOut(fade);
    body.current = next;
  }
}

// Reorders a skinned mesh's triangles into the four clothing groups by the bone that moves each
// vertex most, so one draw per group dresses the body without a second texture.
function groupByBone(mesh) {
  const g = mesh.geometry;
  const joints = g.attributes.skinIndex;
  const weights = g.attributes.skinWeight;
  const bones = mesh.skeleton.bones;
  const index = g.index ? g.index.array : null;
  const count = index ? index.length : g.attributes.position.count;
  const groupOf = (v) => {
    let best = 0;
    let bestW = -1;
    for (let k = 0; k < 4; k++) {
      const w = weights.getComponent(v, k);
      if (w > bestW) {
        bestW = w;
        best = joints.getComponent(v, k);
      }
    }
    const bone = bones[best];
    return bone ? groupOfBone(bone.name) : 3;
  };
  const buckets = [[], [], [], []];
  for (let t = 0; t < count; t += 3) {
    const a = index ? index[t] : t;
    const b = index ? index[t + 1] : t + 1;
    const c = index ? index[t + 2] : t + 2;
    // a triangle at a seam follows the lowest group of its corners: shirt over skin, trousers over shoes
    const group = Math.min(groupOf(a), groupOf(b), groupOf(c));
    buckets[group].push(a, b, c);
  }
  const merged = [];
  g.clearGroups();
  let start = 0;
  buckets.forEach((list, i) => {
    g.addGroup(start, list.length, i);
    for (const v of list) merged.push(v);
    start += list.length;
  });
  g.setIndex(merged);
  return GROUPS;
}
